import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, useSyncExternalStore } from "react";
// Runtime modules are intentionally unbundled so every renderer shares the
// same WASM boundary. This query invalidates CEF profiles that cached the old
// stable URL before /runtime/* was changed to revalidate on every deployment.
import { createLensCoreEngine } from "/runtime/index.js?v=20260902";
import { negotiateCameraTrack } from "./cameraFormat.js";
import { createEngineMetricsStore, useEngineMetricsFromStore } from "./engineMetrics.js";
import { createEngineStatsStore, useFrameStatsFromStore } from "./engineStats.js";
import { createLensErrorMonitor, useLensErrorsFromStore } from "./lensErrors.js";
import { normalizeOrientation } from "./orientation.js";
import { createLensPluginHost } from "./pluginHost.js";
import { LENS_LOAD_ERROR_CODES, LENS_LOAD_STAGES, LensLoadError, asLensLoadError, serializeLensLoadError, } from "./lensLoadError.js";
const createTypedLensCoreEngine = createLensCoreEngine;
const EngineContext = createContext(null);
/**
 * Reads the EngineContext. Throws if used outside <EngineProvider>.
 * @returns {EngineContextValue}
 */
export function useEngine() {
    const value = useContext(EngineContext);
    if (!value) {
        throw new Error("useEngine() must be called inside <EngineProvider>.");
    }
    return value;
}
/**
 * Subscribes to a slice of the engine stats store (frameCount, measuredFps, etc.).
 * Re-renders only when the selected value changes (Object.is comparison).
 *
 * @template T
 * @param {(stats: import("./engineStats.js").EngineStats) => T} [selector]
 * @returns {T}
 *
 * @remarks
 * The selector MUST return primitives or stable references. Returning a new
 * object/array each call (e.g. `s => ({ ...s })`) causes infinite re-renders.
 */
export function useFrameStats(selector) {
    const { statsStore } = useEngine();
    return useFrameStatsFromStore(statsStore, selector);
}
/**
 * Subscribes to LensCore processing-time and completed-frame callback metrics.
 * The underlying store updates every frame but publishes to React at most once
 * per second by default.
 *
 * @template T
 * @param {(metrics: import("./engineMetrics.js").EngineMetricsSnapshot) => T} [selector]
 * @returns {T}
 */
export function useEngineMetrics(selector) {
    const { metrics } = useEngine();
    return useEngineMetricsFromStore(metrics, selector);
}
/** Subscribe to the typed LensCore health/error model or a selected slice. */
export function useLensErrors(selector) {
    const { errorMonitor } = useEngine();
    return useLensErrorsFromStore(errorMonitor, selector);
}
/** Subscribe only to `idle | loading | healthy | degraded | failed`. */
export function useLensHealth() {
    return useLensErrors((snapshot) => snapshot.health);
}
/**
 * Subscribe to a service exposed by an EngineProvider plugin.
 *
 * @template T
 * @param {string} name
 * @returns {T | undefined}
 */
export function useLensPlugin(name) {
    const { pluginHost } = useEngine();
    return useSyncExternalStore(pluginHost.subscribe, () => pluginHost.get(name), () => undefined);
}
function buildConstraints(camera) {
    if (camera?.constraints)
        return camera.constraints;
    const video = {};
    if (camera?.deviceId)
        video.deviceId = { exact: camera.deviceId };
    if (camera?.preferredFormat) {
        video.width = { ideal: camera.preferredFormat.width };
        video.height = { ideal: camera.preferredFormat.height };
        video.frameRate = { ideal: camera.preferredFormat.fps };
    }
    return { video: Object.keys(video).length ? video : true, audio: false };
}
async function loadImageCanvas(url) {
    const sourceUrl = String(url || "").trim();
    if (!sourceUrl)
        throw new Error("Static image URL is required.");
    const image = new Image();
    image.decoding = "async";
    const absoluteUrl = new URL(sourceUrl, window.location.href);
    if (absoluteUrl.origin !== window.location.origin) {
        image.crossOrigin = "anonymous";
    }
    await new Promise((resolve, reject) => {
        image.addEventListener("load", () => resolve(), { once: true });
        image.addEventListener("error", () => reject(new Error(`Failed to load static image: ${sourceUrl}`)), { once: true });
        image.src = sourceUrl;
    });
    const width = image.naturalWidth;
    const height = image.naturalHeight;
    if (!width || !height)
        throw new Error(`Static image has invalid dimensions: ${sourceUrl}`);
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext("2d");
    if (!context)
        throw new Error("Could not create the static image input canvas.");
    context.drawImage(image, 0, 0, width, height);
    return { canvas, width, height, sourceUrl };
}
/**
 * Owns a single LensCoreEngine instance for the subtree.
 *
 * Engine creation is deferred until a child component (typically <LensCanvas>)
 * attaches its <canvas> via the `canvasRef` callback exposed in context.
 * That solves the chicken-and-egg between LensCoreEngineOptions.canvas
 * (required at construction) and the React render order.
 *
 * @param {Object} props
 * @param {Object} [props.options]  Engine options minus `canvas`.
 * @param {Array<Object>} [props.plugins] Stable, ordered plugin objects. Plugins
 *   are activated after LensCore initializes and before `ready` becomes true.
 * @param {ReturnType<typeof createLensErrorMonitor>} [props.errorMonitor]
 *   Optional shared monitor for a diagnostics host. A private core monitor is
 *   created automatically when this is omitted.
 * @param {React.ReactNode} props.children
 */
export function EngineProvider({ options, plugins = [], errorMonitor: providedErrorMonitor, children }) {
    const optionsRef = useRef(options);
    optionsRef.current = options;
    const pluginHostRef = useRef(null);
    if (!pluginHostRef.current)
        pluginHostRef.current = createLensPluginHost(plugins);
    const pluginHost = pluginHostRef.current;
    const errorMonitorRef = useRef(null);
    const ownsErrorMonitorRef = useRef(providedErrorMonitor == null);
    if (!errorMonitorRef.current)
        errorMonitorRef.current = providedErrorMonitor ?? createLensErrorMonitor();
    const errorMonitor = errorMonitorRef.current;
    const [engine, setEngine] = useState(null);
    const [ready, setReady] = useState(false);
    const [error, setError] = useState(null);
    const [source, setSource] = useState(null);
    const statsStore = useMemo(() => createEngineStatsStore(), []);
    const metrics = useMemo(() => createEngineMetricsStore(), []);
    const canvasElementRef = useRef(null);
    const staticImageCanvasRef = useRef(null);
    const runtimeErrorListenersRef = useRef(new Set());
    const frameListenersRef = useRef(new Set());
    const disabledObserversRef = useRef(new Set());
    const logObserverFailure = useCallback((name, failure) => {
        const normalized = failure instanceof Error ? failure : new Error(String(failure));
        try {
            optionsRef.current?.logger?.(`${name} callback threw`, {
                name: normalized.name,
                message: normalized.message,
                stack: normalized.stack ?? null,
            });
        }
        catch { /* a broken logger must not break the renderer */ }
    }, []);
    const invokeObserver = useCallback((name, callback, ...args) => {
        if (typeof callback !== "function" || disabledObserversRef.current.has(name))
            return;
        try {
            callback(...args);
        }
        catch (failure) {
            // In particular, do not repeatedly invoke a broken per-frame observer.
            disabledObserversRef.current.add(name);
            logObserverFailure(name, failure);
        }
    }, [logObserverFailure]);
    const publishRuntimeError = useCallback((failure) => {
        errorMonitor.runtimeError(failure);
        const runtimeError = asLensLoadError(failure, {
            stage: LENS_LOAD_STAGES.RUNTIME,
            code: LENS_LOAD_ERROR_CODES.RUNTIME_EXCEPTION,
            originalPreserved: true,
        });
        setError(runtimeError);
        for (const listener of runtimeErrorListenersRef.current) {
            try {
                listener(runtimeError);
            }
            catch (listenerError) {
                logObserverFailure("Runtime error listener", listenerError);
            }
        }
        pluginHost.emitSync("onLensRuntimeError", { error: runtimeError });
        invokeObserver("onError", optionsRef.current?.onError, runtimeError);
        return runtimeError;
    }, [errorMonitor, invokeObserver, logObserverFailure, pluginHost]);
    const publishAbort = useCallback((failure) => {
        errorMonitor.engineAborted(failure);
        const runtimeError = asLensLoadError(failure, {
            stage: LENS_LOAD_STAGES.RUNTIME,
            code: LENS_LOAD_ERROR_CODES.RUNTIME_EXCEPTION,
            originalPreserved: true,
        });
        setError(runtimeError);
        for (const listener of runtimeErrorListenersRef.current) {
            try {
                listener(runtimeError);
            }
            catch (listenerError) {
                logObserverFailure("Runtime error listener", listenerError);
            }
        }
        pluginHost.emitSync("onLensRuntimeError", { error: runtimeError, fatal: true });
        invokeObserver("onAbort", optionsRef.current?.onAbort, failure);
        invokeObserver("onError", optionsRef.current?.onError, runtimeError);
        return runtimeError;
    }, [errorMonitor, invokeObserver, logObserverFailure, pluginHost]);
    const canvasRef = useCallback((node) => {
        canvasElementRef.current = node;
        if (!node || engine)
            return;
        setError(null);
        try {
            const next = createTypedLensCoreEngine({
                ...optionsRef.current,
                canvas: node,
                logger: (message, detail) => {
                    invokeObserver("logger", optionsRef.current?.logger, message, detail);
                    if (message === "Lens remote asset failed") {
                        publishRuntimeError(new LensLoadError("A remote lens asset could not be loaded", {
                            stage: LENS_LOAD_STAGES.RUNTIME,
                            code: LENS_LOAD_ERROR_CODES.REMOTE_ASSET_FAILED,
                            originalPreserved: true,
                            details: detail,
                        }));
                    }
                    else if (message === "LensCore ERR") {
                        // Native stderr is useful evidence but is not automatically a
                        // failed lens. Structured exception/abort callbacks remain the
                        // authoritative error paths.
                        errorMonitor.acceptEngineLog(message, detail);
                    }
                },
                onError: publishRuntimeError,
                onAbort: publishAbort,
                onFrameProcessed: (event, count) => {
                    metrics.recordFrame(event !== null && typeof event === "object" ? event : null, count);
                    errorMonitor.frameProcessed(count);
                    statsStore.recordFrame(count);
                    for (const listener of frameListenersRef.current) {
                        try {
                            listener({ event, count });
                        }
                        catch (listenerError) {
                            logObserverFailure("Frame listener", listenerError);
                        }
                    }
                    invokeObserver("onFrameProcessed", optionsRef.current?.onFrameProcessed, event, count);
                    pluginHost.emitSync("onFrameProcessed", { event, count });
                },
            });
            setEngine(next);
            next.initialize().then(async () => {
                try {
                    const version = await next.getCoreVersion();
                    statsStore.setCoreVersion(version);
                }
                catch (_versionError) {
                    // non-fatal
                }
                await pluginHost.activate({
                    engine: next,
                    statsStore,
                    metrics,
                    errorMonitor,
                    onPluginError(pluginFailure, plugin, hook) {
                        optionsRef.current?.logger?.("Lens plugin error", {
                            plugin: plugin?.name,
                            hook,
                            error: pluginFailure,
                        });
                    },
                });
                setReady(true);
            }).catch((initError) => {
                const normalized = initError instanceof Error ? initError : new Error(String(initError));
                errorMonitor.engineInitializationFailed(normalized);
                setError(normalized);
            });
        }
        catch (createError) {
            const normalized = createError instanceof Error ? createError : new Error(String(createError));
            errorMonitor.engineInitializationFailed(normalized);
            setError(normalized);
        }
    }, [engine, errorMonitor, invokeObserver, logObserverFailure, metrics, pluginHost, publishAbort, publishRuntimeError, statsStore]);
    useEffect(() => () => metrics.destroy(), [metrics]);
    useEffect(() => errorMonitor.onIssue((issue, snapshot) => {
        optionsRef.current?.onLensIssue?.(issue, snapshot);
    }), [errorMonitor]);
    useEffect(() => () => {
        if (ownsErrorMonitorRef.current)
            errorMonitor.destroy();
    }, [errorMonitor]);
    useEffect(() => () => {
        if (engine) {
            pluginHost.dispose()
                .catch(() => undefined)
                .then(() => engine.destroy())
                .catch(() => undefined);
        }
    }, [engine, pluginHost]);
    const performLensLoad = useCallback(async (mode, lensSource, lensOptions = {}, transaction = {}) => {
        const startedAt = performance.now();
        // Keep a useful identity even when LensCore rejects a URL before it can
        // return its normalized LoadedLensInfo.
        const lensId = lensOptions?.lensId ?? (typeof lensSource === "string" ? lensSource : null);
        errorMonitor.beginLensLoad(lensId);
        const progress = (stage, state, detail = {}) => {
            try {
                transaction.onProgress?.({ stage, state, ...detail });
            }
            catch (progressError) {
                optionsRef.current?.logger?.("Lens load progress callback threw", progressError);
            }
        };
        if (!engine) {
            const notReady = new LensLoadError("Engine is not ready yet.", {
                stage: LENS_LOAD_STAGES.ENGINE,
                code: LENS_LOAD_ERROR_CODES.ENGINE_NOT_READY,
                lensId,
            });
            errorMonitor.loadFailed(notReady);
            throw notReady;
        }
        setError(null);
        const waitsForFirstFrame = transaction.readyWhen === "first-frame";
        let resolveFirstFrame = () => undefined;
        let rejectFirstFrame = () => undefined;
        let firstFrameAt = null;
        let engineLoadComplete = false;
        const runtimeWarnings = [];
        const firstFrame = waitsForFirstFrame ? new Promise((resolve, reject) => {
            resolveFirstFrame = resolve;
            rejectFirstFrame = reject;
        }) : null;
        const runtimeListener = waitsForFirstFrame
            ? (runtimeError) => {
                if (runtimeError.code === LENS_LOAD_ERROR_CODES.REMOTE_ASSET_FAILED) {
                    runtimeWarnings.push(serializeLensLoadError(runtimeError));
                    return;
                }
                rejectFirstFrame(runtimeError);
            }
            : null;
        if (runtimeListener)
            runtimeErrorListenersRef.current.add(runtimeListener);
        const frameListener = waitsForFirstFrame ? () => {
            if (!engineLoadComplete || firstFrameAt !== null)
                return;
            firstFrameAt = performance.now();
            resolveFirstFrame();
        } : null;
        if (frameListener)
            frameListenersRef.current.add(frameListener);
        const userLensEvent = lensOptions?.onLensEvent;
        const preparedOptions = {
            ...lensOptions,
            onLensEvent(event, detail) {
                errorMonitor.lensEvent(event, detail);
                if (event === "firstFrameProcessed") {
                    firstFrameAt = performance.now();
                    resolveFirstFrame(detail);
                }
                try {
                    userLensEvent?.(event, detail);
                }
                catch (callbackError) {
                    optionsRef.current?.logger?.("Lens lifecycle callback threw", callbackError);
                }
            },
        };
        let request = { mode, source: lensSource, options: preparedOptions };
        let modificationFailure = null;
        const modifyStartedAt = performance.now();
        progress(LENS_LOAD_STAGES.MODIFY, "started");
        try {
            request = await pluginHost.prepareLens(request);
            progress(LENS_LOAD_STAGES.MODIFY, "completed", { elapsedMs: performance.now() - modifyStartedAt });
        }
        catch (failure) {
            modificationFailure = asLensLoadError(failure, {
                stage: LENS_LOAD_STAGES.MODIFY,
                lensId,
                originalPreserved: true,
            });
            progress(modificationFailure.stage, "failed", { error: serializeLensLoadError(modificationFailure) });
            await pluginHost.emit("onLensLoadError", { mode, request, error: modificationFailure });
            if (transaction.modificationFailure !== "load-original") {
                errorMonitor.loadFailed(modificationFailure);
                if (runtimeListener)
                    runtimeErrorListenersRef.current.delete(runtimeListener);
                if (frameListener)
                    frameListenersRef.current.delete(frameListener);
                throw modificationFailure;
            }
            errorMonitor.reportIssue({
                code: "MODIFICATION_FAILED",
                severity: "warning",
                source: "host",
                message: modificationFailure.message,
                recoverable: true,
                details: serializeLensLoadError(modificationFailure),
            });
            request = { mode, source: lensSource, options: preparedOptions };
        }
        const modifiedAt = performance.now();
        progress(LENS_LOAD_STAGES.ENGINE, "started");
        let info;
        try {
            info = mode === "add"
                ? await engine.addLens(request.source, request.options)
                : await engine.applyLens(request.source, request.options);
            if (mode === "add") {
                statsStore.setActiveLenses([...statsStore.get().activeLenses.filter((lens) => lens.lensId !== info.lensId), info]);
            }
            else {
                statsStore.setActiveLenses([info]);
            }
            errorMonitor.lensLoaded(info.lensId);
            await pluginHost.emit("onLensLoaded", { mode, request, info });
            progress(LENS_LOAD_STAGES.ENGINE, "completed", { elapsedMs: performance.now() - modifiedAt });
        }
        catch (failure) {
            const loadError = asLensLoadError(failure, {
                stage: LENS_LOAD_STAGES.ENGINE,
                lensId,
                originalPreserved: true,
            });
            if (runtimeListener)
                runtimeErrorListenersRef.current.delete(runtimeListener);
            if (frameListener)
                frameListenersRef.current.delete(frameListener);
            progress(loadError.stage, "failed", { error: serializeLensLoadError(loadError) });
            errorMonitor.loadFailed(loadError);
            await pluginHost.emit("onLensLoadError", { mode, request, error: loadError });
            throw loadError;
        }
        const engineLoadedAt = performance.now();
        engineLoadComplete = true;
        if (waitsForFirstFrame) {
            progress(LENS_LOAD_STAGES.RUNTIME, "started");
            const timeoutMs = Number.isFinite(transaction.timeoutMs) ? Number(transaction.timeoutMs) : 5_000;
            let timer;
            try {
                await Promise.race([
                    firstFrame,
                    new Promise((_, reject) => {
                        timer = setTimeout(() => reject(new LensLoadError(`LensCore accepted the lens but did not process its first frame within ${timeoutMs}ms.`, {
                            stage: LENS_LOAD_STAGES.RUNTIME,
                            code: LENS_LOAD_ERROR_CODES.FIRST_FRAME_TIMEOUT,
                            lensId: info.lensId,
                            originalPreserved: true,
                        })), timeoutMs);
                    }),
                ]);
                progress(LENS_LOAD_STAGES.RUNTIME, "completed", { elapsedMs: performance.now() - engineLoadedAt });
            }
            catch (failure) {
                const runtimeError = asLensLoadError(failure, {
                    stage: LENS_LOAD_STAGES.RUNTIME,
                    lensId: info.lensId,
                    originalPreserved: true,
                });
                setError(runtimeError);
                progress(LENS_LOAD_STAGES.RUNTIME, "failed", { error: serializeLensLoadError(runtimeError) });
                if (runtimeError.code === LENS_LOAD_ERROR_CODES.FIRST_FRAME_TIMEOUT)
                    errorMonitor.loadFailed(runtimeError);
                else
                    errorMonitor.markLoadFailed();
                await pluginHost.emit("onLensLoadError", { mode, request, error: runtimeError });
                throw runtimeError;
            }
            finally {
                if (timer !== undefined)
                    clearTimeout(timer);
                if (runtimeListener)
                    runtimeErrorListenersRef.current.delete(runtimeListener);
                if (frameListener)
                    frameListenersRef.current.delete(frameListener);
            }
        }
        if (runtimeListener)
            runtimeErrorListenersRef.current.delete(runtimeListener);
        if (frameListener)
            frameListenersRef.current.delete(frameListener);
        const completedAt = performance.now();
        return {
            status: "ready",
            ...info,
            info,
            modified: !modificationFailure && request.source !== lensSource,
            modification: modificationFailure
                ? { status: "fallback", error: serializeLensLoadError(modificationFailure) }
                : { status: request.source !== lensSource ? "modified" : "unchanged" },
            timings: {
                modifyMs: modifiedAt - modifyStartedAt,
                engineMs: engineLoadedAt - modifiedAt,
                ...(firstFrameAt === null ? {} : { firstFrameMs: Math.max(0, firstFrameAt - engineLoadedAt) }),
                totalMs: completedAt - startedAt,
            },
            warnings: [...(modificationFailure ? [{
                        code: "MODIFICATION_SKIPPED",
                        message: "Loaded the original lens after modification failed.",
                        error: serializeLensLoadError(modificationFailure),
                    }] : []), ...runtimeWarnings],
        };
    }, [engine, errorMonitor, pluginHost, statsStore]);
    const loadLens = useCallback((lensSource, options = {}) => {
        const { readyWhen = "first-frame", timeoutMs = 5_000, modificationFailure = "fail", onProgress, ...lensOptions } = options;
        return performLensLoad("apply", lensSource, lensOptions, {
            readyWhen,
            timeoutMs,
            modificationFailure,
            onProgress,
        });
    }, [performLensLoad]);
    const applyLens = useCallback(async (lensSource, lensOptions) => (await performLensLoad("apply", lensSource, lensOptions, { readyWhen: "engine" })).info, [performLensLoad]);
    const addLens = useCallback(async (lensSource, lensOptions) => (await performLensLoad("add", lensSource, lensOptions, { readyWhen: "engine" })).info, [performLensLoad]);
    const removeLens = useCallback(async (lensId) => {
        if (!engine)
            return;
        await engine.removeLens(lensId);
        statsStore.setActiveLenses(statsStore.get().activeLenses.filter((l) => l.lensId !== lensId));
        errorMonitor.lensRemoved(lensId);
        await pluginHost.emit("onLensRemoved", { lensId });
    }, [engine, errorMonitor, pluginHost, statsStore]);
    const clearLenses = useCallback(async () => {
        if (!engine)
            return;
        await engine.clearLenses();
        statsStore.setActiveLenses([]);
        errorMonitor.lensesCleared();
        await pluginHost.emit("onLensesCleared", {});
    }, [engine, errorMonitor, pluginHost, statsStore]);
    const setDeviceClass = useCallback(async (deviceClass) => {
        if (!engine)
            throw new Error("Engine is not ready yet.");
        return engine.setDeviceClass(deviceClass);
    }, [engine]);
    const startCamera = useCallback(async (config) => {
        if (!engine)
            throw new Error("Engine is not ready yet.");
        if (!config) {
            engine.stopWebcam();
            setSource(null);
            return null;
        }
        engine.stopWebcam();
        staticImageCanvasRef.current = null;
        const constraints = buildConstraints(config);
        const stream = await engine.startWebcam(constraints);
        const track = stream.getVideoTracks()[0];
        if (!track)
            throw new Error("Camera stream did not contain a video track.");
        const format = config.preferredFormat
            ? await negotiateCameraTrack(track, config.preferredFormat)
            : null;
        const attachOptions = config.preferredFormat
            ? {
                useManualFrameProcessing: false,
                fps: Number(format?.negotiated?.frameRate) || format?.target?.fps || config.preferredFormat.fps,
                explicitRenderSize: {
                    width: Number(format?.negotiated?.width) || format?.target?.width || config.preferredFormat.width,
                    height: Number(format?.negotiated?.height) || format?.target?.height || config.preferredFormat.height,
                },
            }
            : { useManualFrameProcessing: false };
        if (config.orientation !== undefined) {
            attachOptions.externalMediaOrientation = normalizeOrientation(config.orientation);
        }
        await engine.attachMedia(stream, attachOptions);
        const settings = track?.getSettings?.() || {};
        const next = {
            type: "camera",
            deviceId: settings.deviceId || config.deviceId || "",
            label: track?.label || "",
            width: settings.width || config.preferredFormat?.width || 0,
            height: settings.height || config.preferredFormat?.height || 0,
            fps: settings.frameRate || config.preferredFormat?.fps || 0,
            requestedFps: config.preferredFormat?.fps || 0,
            capabilities: {
                width: format?.capabilities?.width || null,
                height: format?.capabilities?.height || null,
                frameRate: format?.capabilities?.frameRate || null,
            },
            capabilityMaxFps: Number(format?.capabilities?.frameRate?.max) || 0,
            negotiation: format?.strategy || "initial",
        };
        setSource(next);
        statsStore.setSource(next);
        await pluginHost.emit("onSourceAttached", next);
        // Host command results cross an iframe postMessage boundary. MediaStream
        // is not structured-cloneable, so returning it makes a successful camera
        // start look like a command timeout to the desktop UI.
        return next;
    }, [engine, pluginHost, statsStore]);
    const attachStaticImage = useCallback(async (config) => {
        if (!engine)
            throw new Error("Engine is not ready yet.");
        const normalized = typeof config === "string" ? { url: config } : config;
        const fps = typeof normalized.fps === "number" && Number.isFinite(normalized.fps) && normalized.fps > 0
            ? Number(normalized.fps)
            : 30;
        setError(null);
        try {
            engine.stopWebcam();
            const loaded = await loadImageCanvas(normalized?.url);
            staticImageCanvasRef.current = loaded.canvas;
            const attachOptions = {
                useManualFrameProcessing: false,
                fps,
            };
            if (normalized?.orientation !== undefined) {
                attachOptions.externalMediaOrientation = normalizeOrientation(normalized.orientation);
            }
            await engine.attachMedia(loaded.canvas, attachOptions);
            const next = {
                type: "image",
                label: loaded.sourceUrl,
                width: loaded.width,
                height: loaded.height,
                fps,
            };
            setSource(next);
            statsStore.setSource(next);
            await pluginHost.emit("onSourceAttached", next);
            return next;
        }
        catch (attachError) {
            staticImageCanvasRef.current = null;
            const normalizedError = attachError instanceof Error ? attachError : new Error(String(attachError));
            setError(normalizedError);
            throw normalizedError;
        }
    }, [engine, pluginHost, statsStore]);
    const stopCamera = useCallback(() => {
        if (!engine)
            return;
        engine.stopWebcam();
        setSource(null);
        statsStore.setSource(null);
        pluginHost.emitSync("onSourceDetached", {});
    }, [engine, pluginHost, statsStore]);
    const setOutputSize = useCallback(async (width, height) => {
        if (!engine)
            return;
        await engine.setExplicitRenderSize(width, height);
    }, [engine]);
    const setFPSLimit = useCallback(async (fps) => {
        if (!engine)
            return;
        await engine.setFPSLimit(fps);
    }, [engine]);
    const actions = useMemo(() => ({
        loadLens,
        applyLens,
        addLens,
        removeLens,
        clearLenses,
        setDeviceClass,
        startCamera,
        attachStaticImage,
        stopCamera,
        setOutputSize,
        setFPSLimit,
    }), [loadLens, applyLens, addLens, removeLens, clearLenses, setDeviceClass, startCamera, attachStaticImage, stopCamera, setOutputSize, setFPSLimit]);
    const value = useMemo(() => ({
        engine,
        canvasRef,
        ready,
        error,
        source,
        actions,
        statsStore,
        metrics,
        errorMonitor,
        pluginHost,
    }), [engine, canvasRef, ready, error, source, actions, statsStore, metrics, errorMonitor, pluginHost]);
    return (_jsx(EngineContext.Provider, { value: value, children: children }));
}
//# sourceMappingURL=EngineProvider.js.map